import { Control } from '@yleisradio/areena-types';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { useUILanguage } from 'hooks/useUILanguage';
import { pointerToCanonicalURL } from 'utils/pointer';
import { useCurrentUrl, usePositionAdjustment } from './hooks';
import styles from './ShareDialog.module.scss';
import {
  ShareMethodCopyLink,
  ShareMethodEmail,
  ShareMethodFacebook,
  ShareMethodLinkedIn,
  ShareMethodTwitter,
  ShareMethodWhatsApp,
} from './ShareMethod';
import { useAreenaService } from 'contexts/AreenaServiceContext';
import { useEscHandler } from 'contexts/EscHandlerContext/EscHandlerContext';

interface Props {
  hide: () => void;
  control: Control;
  isVisible: boolean;
  shareDialogId: string | undefined;
}

export const ShareDialog: React.FunctionComponent<Props> = ({
  hide,
  control,
  isVisible,
  shareDialogId,
}) => {
  const dialogRef = useRef<HTMLElement>(null);
  const wrapperRef = useRef<HTMLElement>(null);
  usePositionAdjustment({
    dialogRef,
    wrapperRef,
    isDialogVisible: isVisible,
  });

  const { areenaService = 'tv' } = useAreenaService();
  const language = useUILanguage();
  const t = useTranslation();
  const currentUrl = useCurrentUrl();
  const { destination: pointer } = control;
  const url =
    (pointer && pointerToCanonicalURL(pointer, language, areenaService)) ||
    currentUrl;
  const onClick = () => hide();

  const { addEventHandler, removeEventHandler } = useEscHandler();
  const eventHandlerTag = 'share-dialog';

  const handleHideDialog = () => {
    removeEventHandler(eventHandlerTag);
    hide();
  };

  useEffect(() => {
    if (isVisible) {
      addEventHandler(hide, eventHandlerTag);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  return (
    <span className={styles.wrapper} ref={wrapperRef}>
      {isVisible ? (
        <span
          className={styles.overlay}
          aria-hidden
          onClick={handleHideDialog}
        />
      ) : null}
      <nav
        aria-label={t('shareTitle')}
        className={styles.dialog}
        hidden={!isVisible}
        ref={dialogRef}
        id={shareDialogId}
      >
        <ul className={styles.list}>
          <li className={styles.row}>
            <ShareMethodTwitter onClick={onClick} url={url} />
          </li>
          <li className={styles.row}>
            <ShareMethodFacebook onClick={onClick} url={url} />
          </li>
          <li className={styles.row}>
            <ShareMethodWhatsApp onClick={onClick} url={url} />
          </li>
          <li className={styles.row}>
            <ShareMethodLinkedIn onClick={onClick} url={url} />
          </li>
          <li className={styles.row}>
            <ShareMethodEmail onClick={onClick} url={url} />
          </li>
          <li className={styles.row}>
            <ShareMethodCopyLink onClick={onClick} url={url} />
          </li>
        </ul>
      </nav>
    </span>
  );
};
